import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  validatePassword,
  validateText,
  validatePasswordEquality,
} from "@/lib/utils/input-validator";
import { getCompanyTaxIdValidationError } from "@/lib/utils/tax-id-validator";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { completeRegistration } from "@/lib/api/handlers/AuthenticationHandling";
import { checkboxDocumentVersions } from "@/lib/legal/documents";
import { useRateLimitCountdown } from "@/hooks/useRateLimitCountdown";
import { useAuth } from "@/lib/auth/AuthContext";
import { hydrateAccountPresentation } from "@/lib/auth/hydrateAccountPresentation";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";

// Full-form state (email + language come from the magic-link token, not here).
const initialState = {
  firstName: "",
  lastName: "",
  country: "",
  password: "",
  confirmPassword: "",
  // B2B-only (Kanzlei 7.1): every self-serve account is a COMPANY. Not a
  // choice any more, so there is no setter path that could flip it.
  customerType: "COMPANY" as const,
  taxId: "",
};

const errorState: {
  length?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  specialChar?: boolean;
} = {
  length: undefined,
  uppercase: undefined,
  lowercase: undefined,
  specialChar: undefined,
};

type FormField = keyof typeof initialState;

/**
 * Email-first step 3 hook. Drives the full signup form behind a validated
 * magic-link token. On success the backend creates the account AND sets the
 * auth cookies (auto-login); we flip AuthContext via markAuthenticated and
 * land on the dashboard — no separate login hop.
 *
 * `invite` is the lane the backend takes at completion (the token's pending
 * row carries an invite code; read from the registration-link response): the
 * DPA box is not asked and the map is terms+privacy, as on the invite form
 * (W9 1-S, audit lens-7 F3); the company block (country, customer type, tax
 * ID) is neither asked, nor required, nor sent — the backend reads none of
 * it for an invite-coded token (lens-8 F7), and the invite form's invite lane
 * sends `undefined` for the three as well. Default false = the company lane.
 */
export function useCompleteSignupForm(token: string, invite = false) {
  const [formData, setFormData] = useState(initialState);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [passwordValidation, setPasswordValidation] = useState(errorState);
  const [isLoading, setIsLoading] = useState(false);
  // The two consent ticks (W9 1-S). Separate on purpose — see
  // DpaConsentCheckbox: the DPA is a distinct contract, and the audit row has
  // to be able to say which of them the person actually saw. The `*Invalid`
  // half means "the person clicked Create without this tick": set only by a
  // failed click, cleared by the tick, so an untouched box is never red and
  // never announced as invalid (⚑A2: validation on click, not "disabled until
  // ticked").
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsInvalid, setTermsInvalid] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [dpaInvalid, setDpaInvalid] = useState(false);
  const termsRef = useRef<HTMLDivElement>(null);
  const dpaRef = useRef<HTMLDivElement>(null);
  const rateLimit = useRateLimitCountdown();
  const t = useTranslations("errors");
  const tBilling = useTranslations("billing");
  const navigate = useNavigate();
  const { markAuthenticated, markMfaRequired } = useAuth();

  // Keep the countdown banner in sync with the ticking clock
  useEffect(() => {
    if (!rateLimit.isLimited) return;
    setFormErrors((prev) => ({
      ...prev,
      password: t("rateLimitedCountdown", { seconds: rateLimit.secondsLeft }),
    }));
  }, [rateLimit.secondsLeft, rateLimit.isLimited, t]);

  const handleInputChange = (field: FormField, value: string) => {
    setFormData((prevState) => ({ ...prevState, [field]: value }));

    if (formErrors[field] || field === "confirmPassword" || field === "password") {
      let errorMessage = "";
      if (["firstName", "lastName"].includes(field)) {
        errorMessage = validateText(value, t);
      } else if (field === "password") {
        setPasswordValidation(validatePassword(value));
      } else if (field === "confirmPassword") {
        errorMessage = validatePasswordEquality(formData.password, value, t);
      }
      setFormErrors((prevErrors) => ({ ...prevErrors, [field]: errorMessage }));
    }
  };

  const handleTermsChange = (checked: boolean) => {
    setTermsAccepted(checked);
    if (checked) setTermsInvalid(false);
  };

  const handleDpaChange = (checked: boolean) => {
    setDpaAccepted(checked);
    if (checked) setDpaInvalid(false);
  };

  const isFormValid = () => {
    const errors: Record<string, string> = {
      firstName: validateText(formData.firstName, t),
      lastName: validateText(formData.lastName, t),
      confirmPassword: validatePasswordEquality(
        formData.password,
        formData.confirmPassword,
        t,
      ),
    };

    // The company block only where a COMPANY is created: account type and
    // country required, the tax ID optional. Not on the invite lane — the
    // fields are not on the screen and the backend ignores them (lens-8 F7).
    if (!invite) {
      if (!formData.customerType) {
        errors.customerType = t("customerTypeRequired");
      }
      if (!formData.country) {
        errors.country = t("countryRequired");
      }
      if (
        formData.customerType === "COMPANY" &&
        formData.taxId.trim() &&
        formData.country.trim()
      ) {
        const taxIdValidationError = getCompanyTaxIdValidationError({
          customerType: formData.customerType,
          country: formData.country,
          taxId: formData.taxId,
        });
        if (taxIdValidationError) {
          errors.taxId = tBilling(`errors.${taxIdValidationError}`);
        }
      }
    }

    setPasswordValidation(validatePassword(formData.password));
    setFormErrors(errors);

    // Consent: the terms+privacy box always, the separate DPA only where a
    // COMPANY is created (not on the invite lane — no box, no fault). A
    // missing tick marks the box (visual + aria + error line) and moves focus
    // to the FIRST one left unticked, so a keyboard or screen-reader user
    // lands on what to do next instead of on a button that did nothing.
    const termsMissing = !termsAccepted;
    const dpaMissing = !invite && !dpaAccepted;
    setTermsInvalid(termsMissing);
    setDpaInvalid(dpaMissing);
    if (termsMissing) termsRef.current?.focus();
    else if (dpaMissing) dpaRef.current?.focus();

    return !Object.values(errors).some((error) => error) && !termsMissing && !dpaMissing;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rateLimit.isLimited) return;
    if (!isFormValid()) return;

    try {
      setIsLoading(true);
      const response = await completeRegistration(
        token,
        formData.firstName,
        formData.lastName,
        formData.password,
        // The company arguments on the company lane only. The invite lane
        // sends none, exactly as the invite form's invite lane does, so the
        // handler's self-serve check and the backend's `!isInvite` branch are
        // never relied on from here (lens-8 F7).
        invite ? undefined : formData.country || undefined,
        invite ? undefined : formData.customerType || undefined,
        invite ? undefined : formData.taxId || undefined,
        // Proof of consent: which documents, in which version, TICKED (W9 1-S).
        // Exactly the boxes that were on the screen: terms+privacy on the
        // invite lane, plus the DPA where a company is created. The backend
        // refuses a short map with `legalConsentIncomplete` and records the
        // accepted one in company_audit_logs as LEGAL_CONSENT_ACCEPTED.
        invite ? checkboxDocumentVersions(["terms", "privacy"]) : checkboxDocumentVersions(),
      );

      if (
        response &&
        "isServerError" in response &&
        response.isServerError === true
      ) {
        if (response.message === "rateLimited" && response.retryAfter) {
          rateLimit.start(response.retryAfter);
        }
        throwServerError(response);
      }

      // MFA branch (e.g. invite into a company with requireMfa).
      if (response && "mfaRequired" in response && response.mfaRequired) {
        markMfaRequired("totp", response.mfaExpiresAt);
        window.dispatchEvent(new Event("navigationStart"));
        navigate(response.mfaSetupRequired ? "/auth/mfa-setup" : "/auth/mfa-verify");
        return;
      }

      // Full auto-login: flip AuthContext THEN navigate (keeps tokenStore +
      // state in lockstep, same as the login form).
      if (response && "companyId" in response) {
        // Hydrate first: this route is under PublicOnlyRoute too.
        await hydrateAccountPresentation({ userId: response.userId });
        markAuthenticated(response.companyId, response.userId);
      }
      window.dispatchEvent(new Event("navigationStart"));
      navigate("/");
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      // The field lines only where the field is on the screen: on the invite
      // lane a company-lane refusal (a pending row that lost its invite code
      // between the link check and this click) falls through to the password
      // line instead of under a field that is not rendered.
      const companyFields = !invite;
      if (
        companyFields &&
        (errorMessage === "invalidTaxId" || errorMessage === "invalidTaxIdCountry")
      ) {
        setFormErrors((prevState) => ({
          ...prevState,
          taxId: tBilling(`errors.${errorMessage}`),
        }));
      } else if (companyFields && errorMessage === "customerTypeIndividualNotOffered") {
        setFormErrors((prevState) => ({
          ...prevState,
          customerType: t("customerTypeIndividualNotOffered"),
        }));
      } else if (
        companyFields &&
        (errorMessage === "customerTypeRequired" || errorMessage === "invalidCustomerType")
      ) {
        setFormErrors((prevState) => ({
          ...prevState,
          customerType: t("customerTypeRequired"),
        }));
      } else if (errorMessage === "legalConsentIncomplete") {
        // The backend's own consent rule said no although every box here was
        // ticked (a lane the form did not expect, or a stale tab whose map
        // predates the rule). Said under the boxes, where the answer is.
        setFormErrors((prevState) => ({
          ...prevState,
          consent: t("legalConsentIncomplete"),
        }));
      } else {
        // invalidToken / tokenExpired / userAlreadyExists / unknown all surface
        // here; the user can restart from /auth/register via the form footer.
        setFormErrors((prevState) => ({
          ...prevState,
          password: translateBackendError(t, error instanceof Error ? error : "unknown"),
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    formErrors,
    passwordValidation,
    isLoading,
    handleInputChange,
    handleSubmit,
    rateLimited: rateLimit.isLimited,
    termsAccepted,
    termsInvalid,
    dpaAccepted,
    dpaInvalid,
    handleTermsChange,
    handleDpaChange,
    termsRef,
    dpaRef,
  };
}
